import React, { useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material'; // Import Snackbar and Alert

const PriceDisplay = ({ selectedTile, area, options, onPreOrderSubmit, onCalculatePrice }) => {
    const [showPreOrderForm, setShowPreOrderForm] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        city: '',
        address: '',
        postalCode: '',
    });
    const [areaSize, setAreaSize] = useState(0);
    const [tilesNeeded, setTilesNeeded] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false); // For controlling the snackbar visibility

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePreOrderClick = () => {
        setShowPreOrderForm(true);
    };

    const handleCloseForm = () => {
        setShowPreOrderForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Collect all data
        const allData = {
            selectedTile,
            area,
            options,
            formData,
            totalPrice,
        };

        try {
            const response = await fetch('https://d3ejxze7429gw1.cloudfront.net/api/pre-order', { // Replace with your server URL
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(allData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Success:', data);

            // Optionally, clear the form or show a success message
            setFormData({
                fullName: '',
                email: '',
                phone: '',
                city: '',
                address: '',
                postalCode: '',
            });

            // Open the snackbar with success message
            setSnackbarOpen(true);

            handleCloseForm();
        } catch (error) {
            console.error('Error submitting pre-order:', error);
            // Handle the error (e.g., show an error message to the user)
        }
    };

    // Function to parse the dimensions from the "size" string in tiles.json
    const parseTileSize = (sizeString) => {
        const [width, height] = sizeString.split('x').map(Number); // Split the string by 'x' and convert to numbers
        return { width, height };
    };

    // useEffect hook to calculate the area, tiles needed, and total price
    useEffect(() => {
        if (selectedTile && area.length && area.width) {

        const { width: tileWidthCm, height: tileHeightCm } = parseTileSize(selectedTile.size);

        const tileWidthMeters = tileWidthCm / 100;
        const tileHeightMeters = tileHeightCm / 100;

        const tileArea = tileWidthMeters * tileHeightMeters; // Calculate the area of a single tile in m²
            const calculatedArea = parseFloat(area.length) * parseFloat(area.width);
            const calculatedTilesNeeded = Math.ceil(calculatedArea / tileArea);
            let calculatedTotalPrice = calculatedTilesNeeded * selectedTile.price;

            if (options.installation) calculatedTotalPrice += parseInt(options.installation);
            if (options.transport) calculatedTotalPrice += parseInt(options.transport);

            setAreaSize(calculatedArea);
            setTilesNeeded(calculatedTilesNeeded);
            setTotalPrice(calculatedTotalPrice);
        }
    }, [area, options, selectedTile]);

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    if (!selectedTile || !area.length || !area.width) {
        return <p className="price-display-message">Fill out all the information to see the price.</p>;
    }

    return (
        <div className="price-display">
            <h2 className="price-display-title">Price Summary</h2>
            <div className="price-display-details">
                <p className="price-display-info">Tile: {selectedTile.name}</p>
                <p className="price-display-info">Area: {area.length} x {area.width} m²</p>
                <p className="price-display-info">Tiles Needed: {tilesNeeded}</p>
            </div>
            <p className="price-display-total">Total Price: ${totalPrice}</p>
            <button className="pre-order-button" onClick={handlePreOrderClick}>
                Pre-Order
            </button>

            {showPreOrderForm && (
                <div className="pre-order-form-overlay">
                    <div className="pre-order-form">
                        <h2 className="pre-order-form-title">Pre-Order Information</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name:</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email:</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Phone Number:</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="city">City:</label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="address">Address:</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="postalCode">Postal Code:</label>
                                <input
                                    type="text"
                                    id="postalCode"
                                    name="postalCode"
                                    value={formData.postalCode}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <button type="submit" className="form-button">Submit Pre-Order</button>
                        </form>
                        <button className="close-button" onClick={handleCloseForm}>
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Snackbar for Success Message */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: 'top', // Position it at the top
                    horizontal: 'center', // Center it horizontally
                }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Your pre-order is successful! One of our teammates will contact you in less than 15 minutes.
                </Alert>
            </Snackbar>
        </div>
    );
};

export default PriceDisplay;